/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useState } from 'react';
import { Monitor, RefreshCw, Search, Trash2, UserSearch } from 'lucide-react';
import { api } from '../lib/api';
import { errorText } from '../lib/errors';
import {
  filterInstalls,
  formatAge,
  installsCapNote,
  installsPersonLookup,
  installStatusText,
  officeHostLabel,
  revokeConfirmText,
  sortInstalls,
  type OfficeInstall,
} from '../lib/governance';
import { SaveNote } from '../components/SaveNote';
import { Button, Callout, EmptyState, Icon, Input, PageHeader, Panel, Stack, StatusChip, Table, Tag, type Column } from '../ui';
import { count, dateTime } from '../ui/format';

/**
 * Office installs (contract §1.4-§1.5): every enrolled Gyro add-in install, one per person per Office app and device,
 * with a sticky revoke (DELETE /v1/admin/office/installs/:installId, audited office.install_revoked). Key material never
 * leaves the service. The console shows this screen only when the service answers the list (App.tsx probes it): a
 * service without the Office add-in answers 404 and the screen stays hidden. Rebuilt on the kit (overhaul step 4): the
 * same calls, the search and the person lookup in the panel's toolbar.
 */
export function OfficeInstalls() {
  const [rows, setRows] = useState<OfficeInstall[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // The person the service filtered the list by (its ?user=), or undefined for the whole list (capped at 1,000 rows).
  const [person, setPerson] = useState<string | undefined>(undefined);

  const load = async (filter: string | undefined = person) => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await api.officeInstalls(filter);
      setRows(result.installs ?? []);
      setPerson(filter);
      setNow(Date.now());
    } catch (error) {
      setLoadError(errorText(error, 'The Office installs could not be read.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const revoke = async (install: OfficeInstall) => {
    if (!window.confirm(revokeConfirmText(install))) return;
    setBusy(install.installId);
    setNote(null);
    try {
      const result = await api.revokeOfficeInstall(install.installId);
      setNote({
        ok: true,
        text: result.status === 'already_revoked'
          ? `${install.ownerEmail}'s ${officeHostLabel(install.host)} install was already revoked.`
          : `Revoked ${install.ownerEmail}'s ${officeHostLabel(install.host)} install. It stops working at once.`,
      });
      await load();
    } catch (error) {
      setNote({ ok: false, text: errorText(error, 'Nothing was revoked.') });
    } finally {
      setBusy(null);
    }
  };

  const visible = sortInstalls(filterInstalls(rows, search));
  const active = rows.filter((row) => row.status === 'active').length;
  const lookup = installsPersonLookup(search);
  const capNote = installsCapNote(rows.length, person);

  const columns: Array<Column<OfficeInstall>> = [
    { key: 'person', header: 'Person', cell: (install) => <span className="ui-cell-stack"><span>{install.ownerEmail}</span><span title={`Install ${install.installId}`}><Tag>{install.installId.slice(0, 8)}…</Tag></span></span> },
    { key: 'app', header: 'App', cell: (install) => officeHostLabel(install.host) },
    { key: 'platform', header: 'Platform', muted: true, hideOnPhone: true, cell: (install) => install.platform || '—' },
    { key: 'versions', header: 'Versions', muted: true, hideOnPhone: true, cell: (install) => <span className="ui-cell-stack"><span>Office {install.officeVersion || '—'}</span><span className="ui-cell-sub">add-in {install.addinVersion || '—'}</span></span> },
    {
      key: 'status', header: 'Status',
      cell: (install) => (
        <span className="ui-cell-stack">
          <span>{install.status === 'active' ? <StatusChip tone="ok">Active</StatusChip> : <StatusChip tone="neutral">{installStatusText(install)}</StatusChip>}</span>
          {install.revokedAt ? <span className="ui-cell-sub">{dateTime(install.revokedAt)}</span> : null}
        </span>
      ),
    },
    { key: 'seen', header: 'Last used', muted: true, cell: (install) => <span className="ui-cell-stack" title={dateTime(install.lastSeenAt)}><span>{formatAge(install.lastSeenAt, now)}</span><span className="ui-cell-sub">enrolled {formatAge(install.createdAt, now)}</span></span> },
    {
      key: 'actions', header: <span className="ui-sr">Actions</span>, align: 'end',
      cell: (install) => (install.status === 'active' ? (
        <Button
          variant="danger"
          size="sm"
          icon={Trash2}
          busy={busy === install.installId}
          aria-label={`Revoke ${install.ownerEmail}'s ${officeHostLabel(install.host)} install`}
          title="Revoke this install (asks first)"
          onClick={() => void revoke(install)}
        >
          Revoke
        </Button>
      ) : null),
    },
  ];

  return (
    <>
      <PageHeader
        meta={<span>Every Gyro add-in install in Outlook, Word, Excel and PowerPoint</span>}
        actions={<Button icon={RefreshCw} busy={loading} onClick={() => void load()} title="Reload the list" aria-label="Refresh" className="is-collapse-phone">Refresh</Button>}
      />
      <div className="ui-page">
        <Stack>
          <Panel
            flush
            title={<span className="ui-title-icon"><Monitor size={16} strokeWidth={1.75} aria-hidden="true" />Installs</span>}
            description={loading ? 'Loading…' : `${count(active)} active of ${count(rows.length)}`}
            footer={<span>Revoking an install stops it at once and is permanent; the person can open the add-in again to enrol a new install under their own sign-in. Each revoke is in the audit trail.</span>}
          >
            <div className="ui-panel-pad ui-panel-pad--divided">
              <div className="ui-form">
                <div className="ui-actions">
                  <Input className="ui-grow" prefix={<Icon icon={Search} size="sm" />} aria-label="Search installs" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Person, install id, app or platform" />
                  <Button
                    icon={UserSearch}
                    disabled={loading || !lookup || lookup === person}
                    onClick={() => void load(lookup)}
                    title="Ask the service for every install of this person (an e-mail address or object id)"
                  >
                    Look up person
                  </Button>
                </div>
                {person && (
                  <Callout tone="info" action={<Button size="sm" disabled={loading} onClick={() => void load(undefined)}>Show all installs</Button>}>
                    Showing every install of <strong>{person}</strong>.
                  </Callout>
                )}
                {capNote && <p className="ui-cell-sub" role="note">{capNote}</p>}
                {loadError && <Callout tone="bad" role="alert">{loadError}</Callout>}
              </div>
            </div>
            <Table stacked
              label="Office installs"
              columns={columns}
              rows={visible}
              rowKey={(install) => install.installId}
              loading={loading}
              skeletonRows={4}
              className="ui-table--tall"
              empty={loadError ? undefined : <EmptyState compact icon={Monitor} title={rows.length === 0 ? 'No Office add-in installs yet' : 'No install matches that search'}>{rows.length === 0 ? 'Installs appear here as people open the add-in.' : 'Try another word, or look the person up.'}</EmptyState>}
            />
            <div className="ui-panel-pad ui-panel-pad--top ui-panel-pad--note"><SaveNote note={note} /></div>
          </Panel>
        </Stack>
      </div>
    </>
  );
}
